import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { UserContext } from "../context/UserContext";
import { useLocation, useNavigate } from "react-router-dom";
import apiFetch from "../context/api";
import LeafgateLogo from "./LeafgateLogo";
import useUserTokenPrivileges from "./hooks/useUserTokenPrivileges";
import { useVersionNueva } from "../lib/hooks/useVersionNueva";
import { getDefaultVenuePath } from "../lib/consts/navigation";
import { DEFAULT_VENUE_TYPE } from "../lib/consts/venueTypes";
import AIChatMockup from "./AIChatMockup";
import { isCognitoEnabled, cognitoSignIn, cognitoForgotPassword } from "../lib/cognito";
import { isValidEmail } from "../lib/email";
import { guardarSesion } from "../lib/authSession";
import "../styles/login.css";

const getConnectionError = () => {
  if (typeof window === "undefined") {
    return "No se pudo conectar al servidor. Comprueba tu conexión o intenta de nuevo.";
  }
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return "No se pudo conectar al backend. ¿Está corriendo en http://localhost:8000?";
  }
  return `No se pudo conectar al servidor. Usa http://${host}:3000 (no :8000) y comprueba que npm start y uvicorn están activos en el PC.`;
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setToken] = useContext(UserContext);
  const { token, user, accessPrivileges, isLoading: isResolvingSession } = useUserTokenPrivileges();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [forgotMessage, setForgotMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const forgotMode = useMemo(() => location.hash === "#forgot", [location.hash]);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const forgotEmailRef = useRef(null);

  // spec: entradas-control#version-nueva — aquí no hay sesión que perder: si hay un frontend
  // más nuevo publicado, se recarga sin preguntar… mientras no haya nada escrito ni un envío
  // en vuelo (la comprobación también salta al volver a la pestaña, en mitad de teclear). Y
  // una sola vez por versión: durante la ventana del despliegue la ruta puede volver con el
  // bundle viejo, y recargar en bucle dejaría el login inservible.
  const { hayVersionNueva, recargaAgotada, recargar: recargarVersion } = useVersionNueva();
  const sinNadaQuePerder = !email && !password && !forgotEmail && !isLoading && !isForgotLoading;
  useEffect(() => {
    if (hayVersionNueva && !recargaAgotada && sinNadaQuePerder) recargarVersion();
  }, [hayVersionNueva, recargaAgotada, sinNadaQuePerder, recargarVersion]);

  useEffect(() => {
    // Mientras UserContext resuelve el token no se navega: el rol podría ser el de la
    // sesión anterior (ver useUserTokenPrivileges).
    if (isResolvingSession) return;
    if (!token || !user) return;

    const fallbackRoute = accessPrivileges === "superadmin"
      ? "/my_shops"
      : getDefaultVenuePath(user?.venue?.venue_type || DEFAULT_VENUE_TYPE);
    const nextRoute = location.state?.from?.pathname || fallbackRoute;
    navigate(nextRoute, { replace: true });
  }, [token, user, accessPrivileges, isResolvingSession, location.state, navigate]);

  const submitLogin = async () => {
    setErrorMessage("");
    setIsLoading(true);

    if (!email) {
      setErrorMessage("Por favor, ingresa tu email");
      setIsLoading(false);
      emailRef.current?.focus();
      return;
    }

    if (!isValidEmail(email)) {
      setErrorMessage("Por favor, ingresa un email válido");
      setIsLoading(false);
      emailRef.current?.focus();
      return;
    }

    if (!password) {
      setErrorMessage("Por favor, ingresa tu contraseña");
      setIsLoading(false);
      passwordRef.current?.focus();
      return;
    }

    try {
      if (isCognitoEnabled) {
        // ── Cognito auth path ────────────────────────────────────────────────
        const { idToken, refreshToken } = await cognitoSignIn(email, password);
        // Store tokens — idToken is the Bearer token the backend validates.
        // refreshToken is stored separately so UserContext can renew the session.
        guardarSesion({ token: idToken, refreshToken, email });
        setToken(idToken);
      } else {
        // ── Local auth path (staging / local dev) ────────────────────────────
        const response = await apiFetch("/api/token", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ username: email, password }).toString(),
        });

        const text = await response.text();

        if (!response.ok) {
          try {
            const data = JSON.parse(text);
            const msg = Array.isArray(data.detail)
              ? data.detail.map((d) => d.msg || d).join(" ")
              : data.detail || "Error al iniciar sesión";
            setErrorMessage(msg);
          } catch {
            if (response.status === 404 || text.includes("<!DOCTYPE") || text.includes("<html")) {
              setErrorMessage(getConnectionError());
            } else {
              setErrorMessage("Error al iniciar sesión. Por favor, intenta de nuevo.");
            }
          }
          return;
        }

        const data = JSON.parse(text);
        if (data.access_token) {
          setToken(data.access_token);
          guardarSesion({ token: data.access_token });
          navigate("/dashboard");
        } else {
          setErrorMessage("No se recibió el token de acceso");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage(error.message || "Error de conexión. No se pudo conectar al servidor.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitLogin();
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setForgotMessage("");

    if (!forgotEmail) {
      setErrorMessage("Por favor, ingresa tu email");
      forgotEmailRef.current?.focus();
      return;
    }

    if (!isValidEmail(forgotEmail)) {
      setErrorMessage("Por favor, ingresa un email válido");
      forgotEmailRef.current?.focus();
      return;
    }

    setIsForgotLoading(true);

    try {
      if (isCognitoEnabled) {
        await cognitoForgotPassword(forgotEmail);
        setForgotMessage(
          "Si existe una cuenta con ese email, te hemos enviado un código de verificación."
        );
      } else {
        const response = await apiFetch("/v1/users/forgot-password", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: forgotEmail }),
        });
        const text = await response.text();
        const data = text ? JSON.parse(text) : {};

        if (!response.ok) {
          setErrorMessage(data.detail || "No se pudo enviar el correo de recuperación.");
        } else {
          setForgotMessage(
            data.message ||
              "Si existe una cuenta con ese email, te hemos enviado un enlace para restablecer la contraseña."
          );
        }
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setErrorMessage(error.message || "No se pudo enviar el correo de recuperación.");
    } finally {
      setIsForgotLoading(false);
    }
  };

  const goToForgot = (e) => {
    e.preventDefault();
    setErrorMessage("");
    setForgotMessage("");
    navigate("/login#forgot", { replace: true });
  };

  const goToLogin = (e) => {
    e.preventDefault();
    setErrorMessage("");
    setForgotMessage("");
    navigate("/login", { replace: true });
  };

  return (
    <div className="login-page">
      <div className="login-left">
        <div className="login-card">
        <div className="login-brand">
          <LeafgateLogo size={32} color="#191A1F" />
          <span className="login-brand-name">ClubsAgent</span>
        </div>

        <h2 className="login-title">
          {forgotMode ? "Recupera tu contraseña" : "Continúa con email"}
        </h2>
        <p className="login-description">
          {forgotMode
            ? "Introduce tu email y te enviaremos un enlace para restablecer tu contraseña."
            : "Inicia sesión con tu email para empezar tu viaje con ClubsAgent y conectar con tu comunidad del club."}
        </p>

        {!forgotMode ? (
          <form onSubmit={handleSubmit} className="login-form" noValidate>
            {errorMessage && (
              <p className="login-error" role="alert">
                {errorMessage}
              </p>
            )}

            <div className="login-field">
              <label htmlFor="email" className="login-label">
                Email
              </label>
              <input
                id="email"
                ref={emailRef}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input"
                placeholder="Introduce tu email"
                autoComplete="email"
              />
            </div>

            <div className="login-field">
              <label htmlFor="password" className="login-label">
                Contraseña
              </label>
              <input
                id="password"
                ref={passwordRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                placeholder="Contraseña"
                autoComplete="current-password"
              />
              <div className="login-forgot-wrap">
                <a href="/login#forgot" onClick={goToForgot} className="login-forgot">
                  ¿Olvidaste la contraseña?
                </a>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || isResolvingSession}
              className="login-submit"
            >
              {isLoading ? "Iniciando sesión..." : isResolvingSession ? "Comprobando sesión..." : "Continuar"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotSubmit} className="login-form" noValidate>
            {errorMessage && (
              <p className="login-error" role="alert">
                {errorMessage}
              </p>
            )}
            {forgotMessage && (
              <p className="login-success" role="status">
                {forgotMessage}
              </p>
            )}

            <div className="login-field">
              <label htmlFor="forgot-email" className="login-label">
                Email
              </label>
              <input
                id="forgot-email"
                ref={forgotEmailRef}
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="login-input"
                placeholder="Introduce tu email"
                autoComplete="email"
              />
            </div>

            <button
              type="submit"
              disabled={isForgotLoading}
              className="login-submit"
            >
              {isForgotLoading ? "Enviando..." : "Enviar enlace de recuperación"}
            </button>

            <button type="button" onClick={goToLogin} className="login-secondary">
              Volver al login
            </button>
          </form>
        )}

        </div>
      </div>
      <div className="login-right">
        <AIChatMockup />
      </div>
    </div>
  );
}
